import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ChevronDown, ChevronRight, History, Loader2, RefreshCw } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtD = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const fmtDT = (v) => (v ? new Date(String(v).match(/Z|\+/) ? v : `${v}Z`).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const RESULT = { auto_approved: ["Auto-approved", "bg-emerald-50 text-emerald-700 border-emerald-200"], not_eligible: ["Went to L1/L2", "bg-amber-50 text-amber-800 border-amber-200"] };

function Filters({ f, setF, onRun, busy }) {
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  return (
    <div className="flex flex-wrap items-end gap-3 text-xs bg-white border border-slate-200 rounded-sm p-3" data-testid="aar-filters">
      <label className="flex flex-col gap-1">From<Input type="date" value={f.date_from} onChange={set("date_from")} className="h-8 w-40" data-testid="aar-from" /></label>
      <label className="flex flex-col gap-1">To<Input type="date" value={f.date_to} onChange={set("date_to")} className="h-8 w-40" data-testid="aar-to" /></label>
      <label className="flex flex-col gap-1">Created by<Input value={f.creator} onChange={set("creator")} placeholder="Name or emp no" className="h-8 w-48" data-testid="aar-creator" /></label>
      <label className="flex flex-col gap-1">Supplier<Input value={f.supplier} onChange={set("supplier")} placeholder="Name or code" className="h-8 w-48" data-testid="aar-supplier" /></label>
      <label className="flex flex-col gap-1">PR amount (₹)
        <span className="flex items-center gap-1">
          <Input type="number" min="0" value={f.amount_min} onChange={set("amount_min")} placeholder="Min" className="h-8 w-28" data-testid="aar-amount-min" />
          <span className="text-slate-400">–</span>
          <Input type="number" min="0" value={f.amount_max} onChange={set("amount_max")} placeholder="Max" className="h-8 w-28" data-testid="aar-amount-max" />
        </span>
      </label>
      <label className="flex flex-col gap-1">Result
        <select value={f.result} onChange={set("result")} className="h-8 border border-slate-300 rounded-sm px-2" data-testid="aar-result">
          <option value="auto_approved">Auto-approved</option><option value="not_eligible">Went to L1/L2</option><option value="all">All checked</option>
        </select>
      </label>
      <label className="flex items-center gap-2 h-8 cursor-pointer"><input type="checkbox" checked={f.edited_only} onChange={set("edited_only")} data-testid="aar-edited-only" /> Changed PRs only</label>
      <Button size="sm" onClick={onRun} disabled={busy} className="bg-cyan-700 hover:bg-cyan-800 h-8" data-testid="aar-run-btn">
        {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />} Run
      </Button>
    </div>
  );
}

function Timeline({ r }) {
  return (
    <div className="grid md:grid-cols-2 gap-4 p-3 bg-slate-50" data-testid={`aar-detail-${r.voc_no}`}>
      <div>
        <div className="text-[10px] uppercase text-slate-500 mb-1">Items (current)</div>
        <table className="w-full text-xs"><tbody>
          {r.items.map((i) => (
            <tr key={i.icode} className="border-t border-slate-200">
              <td className="py-1 font-mono">{i.icode}</td><td className="text-slate-600">{i.iname}</td>
              <td className="text-right">{i.qty} {i.unit}</td><td className="text-right font-mono">@ {inr(i.rate)}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      <div>
        <div className="text-[10px] uppercase text-slate-500 mb-1">History (past → recent)</div>
        <ol className="space-y-1.5 text-xs">
          {r.timeline.map((t, n) => (
            <li key={n} className="border-l-2 pl-2 border-slate-300" data-testid={`aar-tl-${r.voc_no}-${n}`}>
              <span className="text-slate-500">{fmtDT(t.at)}</span>{" "}
              {t.kind === "edit" ? (
                <><b className="text-cyan-800">Edited</b> by {t.by_name || t.by}: {t.changes.length ? t.changes.join(" · ") : "no field change"}</>
              ) : (
                <><b className={t.result === "auto_approved" ? "text-emerald-700" : "text-amber-700"}>{t.result === "auto_approved" ? "Auto-approved" : "Check failed"}</b>
                  {" "}· supplier {t.supplier} · ₹{inr(t.total_inr)}{t.reasons.length ? ` · ${t.reasons.slice(0, 3).join("; ")}` : ""}</>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function Row({ r, open, toggle }) {
  const [label, cls] = RESULT[r.final_result] || [r.final_result, "bg-slate-100 text-slate-600 border-slate-200"];
  return (
    <Fragment>
      <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={toggle} data-testid={`aar-row-${r.voc_no}`}>
        <td className="px-2 py-2">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td className="px-2 py-2 font-mono font-semibold">#{r.voc_no}<div className="font-sans font-normal text-slate-500">{r.purc_type}</div></td>
        <td className="px-2 py-2 whitespace-nowrap">{fmtD(r.pr_date)}</td>
        <td className="px-2 py-2">{r.site}</td>
        <td className="px-2 py-2"><div className="font-mono">{r.supplier_code}</div><div className="text-slate-500 truncate max-w-[200px]">{r.supplier_name}</div></td>
        <td className="px-2 py-2 max-w-[260px]">{r.items.slice(0, 2).map((i) => <div key={i.icode} className="truncate"><span className="font-mono">{i.icode}</span> <span className="text-slate-500">{i.iname}</span></div>)}{r.items.length > 2 && <div className="text-slate-400">+{r.items.length - 2} more</div>}</td>
        <td className="px-2 py-2">{(r.created_by_name || "").replace(/\s*\(\d+\)\s*$/, "")}<div className="text-slate-400 font-mono">#{r.created_by}</div></td>
        <td className="px-2 py-2 text-right font-mono whitespace-nowrap">₹{inr(r.total)}{r.currency !== "INR" && <div className="text-slate-400">{r.currency}</div>}</td>
        <td className="px-2 py-2"><span className={`px-1.5 py-0.5 rounded-full border text-[10px] whitespace-nowrap ${cls}`}>{label}</span><div className="text-slate-400 mt-0.5">{r.status}</div></td>
        <td className="px-2 py-2 whitespace-nowrap" data-testid={`aar-changed-${r.voc_no}`}>
          {r.changed ? <span className="text-cyan-800 inline-flex items-center gap-1"><History size={12} /> Changed · {fmtDT(r.last_change_at)}</span> : <span className="text-slate-400">No change</span>}
        </td>
      </tr>
      {open && <tr><td colSpan={10}><Timeline r={r} /></td></tr>}
    </Fragment>
  );
}

export default function AutoApproveReport() {
  const now = new Date();
  const [f, setF] = useState({ date_from: isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30)), date_to: isoDay(now), creator: "", supplier: "", amount_min: "", amount_max: "", result: "auto_approved", edited_only: false });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});
  const run = useCallback(async () => {
    setBusy(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const { data } = await api.get("/reports/auto-approve-prs", { params, timeout: 120000 });
      setRows(data.rows || []); setOpen({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load report"); }
    finally { setBusy(false); }
  }, [f]);
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const total = rows.reduce((a, r) => a + r.total, 0);
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-cyan-800 text-white px-4 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/sap" className="inline-flex items-center gap-1 text-sm text-cyan-100 hover:text-white" data-testid="aar-back"><ArrowLeft size={16} /> SAP Workspace</Link>
        <h1 className="text-base sm:text-lg font-semibold">Auto-Approve PR Report</h1>
      </header>
      <main className="max-w-[1500px] mx-auto p-3 sm:p-6 space-y-4">
        <Filters f={f} setF={setF} onRun={run} busy={busy} />
        <div className="text-xs text-slate-600" data-testid="aar-summary">{rows.length} PR(s) · ₹{inr(total)} · {rows.filter((r) => r.changed).length} changed</div>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="aar-table">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 text-left">
              <tr>{["", "PR", "PR date", "Site", "Supplier", "Items", "Created by", "Total", "Result", "Changes"].map((h, i) => <th key={i} className="px-2 py-2">{h}</th>)}</tr>
            </thead>
            <tbody>
              {!busy && rows.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500" data-testid="aar-empty">No PRs for these filters.</td></tr>}
              {rows.map((r) => <Row key={r.voc_no} r={r} open={!!open[r.voc_no]} toggle={() => setOpen((p) => ({ ...p, [r.voc_no]: !p[r.voc_no] }))} />)}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400">Field-level edit details are recorded from 31-Oct-2026; earlier changes show as auto-approve checks with the supplier and total at each check.</p>
      </main>
    </div>
  );
}
